import { useDepartmentText } from 'model/DepartmentTerminology';
import { Box, Group, Modal, Select, Stack } from '@mantine/core';
import { IFileState } from 'components/files/upload/types';
import UploadFiles from 'components/files/upload/UploadFiles';
import { noop } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { DEVICE_FILE_TYPE_NAME_MAP, DeviceFileType, isUploadableDeviceFileType } from 'model/DeviceFileType';
import Button from 'components/Button';

/**
 * The parameters of the add device file modal.
 */
export interface AddDeviceFileModalParams {
  onAdd?: (files: IFileState[], fileType: DeviceFileType) => void;
  fileType?: DeviceFileType;
  allowedFileTypes?: DeviceFileType[];
  opened: boolean;
  onClose: () => void;
}

/**
 * Modal for adding a file.
 */
export default function AddDeviceFileModal({
  onAdd = noop,
  fileType: initialFileType = 'project-documentation',
  allowedFileTypes,
  opened,
  onClose,
}: AddDeviceFileModalParams) {
  const departmentText = useDepartmentText();
  const [files = [], setFiles] = useState<IFileState[] | undefined>([]);
  const [selectedFileType, setSelectedFileType] = useState<DeviceFileType>(initialFileType);

  const fileTypeSelectData = useMemo(
    () =>
      (Object.entries(DEVICE_FILE_TYPE_NAME_MAP) as [DeviceFileType, string][])
        .filter(([value]) => isUploadableDeviceFileType(value))
        .filter(([value]) => !allowedFileTypes || allowedFileTypes.includes(value))
        .map(([value, label]) => ({
          value,
          label: value === 'department' ? departmentText(label, 'Ostatná dokumentácia zo strediska') : label,
        })),
    [allowedFileTypes, departmentText]
  );

  const hasAllowedFileType = fileTypeSelectData.length > 0;

  const loading = useMemo(() => files.some(({ finalized }) => !finalized), [files]);
  const disabled = useMemo(() => files.length === 0 || !hasAllowedFileType, [files, hasAllowedFileType]);

  const submit = useCallback(() => {
    onAdd(files, selectedFileType);
    onClose();
  }, [files, selectedFileType, onAdd, onClose]);

  useEffect(() => {
    if (!opened) {
      setFiles([]);
    }
  }, [opened]);

  useEffect(() => {
    const isInitialFileTypeAllowed = fileTypeSelectData.some(({ value }) => value === initialFileType);

    if (isInitialFileTypeAllowed) {
      setSelectedFileType(initialFileType);
      return;
    }

    const firstAllowed = fileTypeSelectData[0]?.value;
    if (firstAllowed) {
      setSelectedFileType(firstAllowed);
    }
  }, [initialFileType, fileTypeSelectData]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Pridať súbory"
      size="auto"
      closeOnClickOutside={!loading}
      closeOnEscape={!loading}
      closeButtonProps={{ loading }}
    >
      <Stack spacing={24}>
        <Select
          label="Typ súboru"
          size="md"
          data={fileTypeSelectData}
          value={hasAllowedFileType ? selectedFileType : null}
          onChange={(value) => setSelectedFileType(value as DeviceFileType)}
          disabled={!hasAllowedFileType}
          withinPortal
        />
        <Box pt={12}>
          <UploadFiles onChange={setFiles} />
        </Box>
        <Group position="right">
          <Button variant="subtle-gray" onClick={onClose} disabled={loading}>
            Zrušiť
          </Button>
          <Button
            variant="primary"
            type="submit"
            onClick={loading ? noop : submit}
            loading={loading}
            disabled={disabled}
          >
            Pridať súbory
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
